import moment from 'moment';
import React from 'react';
import { Line, defaults } from 'react-chartjs-2';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Navbar from 'react-bootstrap/Navbar';

import { data } from './data';

type CityConfig = { colour: string, title: string };

const cities: { [key: string]: CityConfig } = {
  "sydney": { colour: '#F78D1E', title: 'Sydney' },
  "melbourne": { colour: '#00ABBD', title: 'Melbourne' },
  "brisbane": { colour: '#00707E', title: 'Brisbane' },
  "perth": { colour: '#6d6e71', title: 'Perth' },
};

type ChartConfig = { title: string };

const charts: { [key: string]: ChartConfig } = {
  "cbd": { title: "CBD" },
  "shopping_centres": { title: "Suburban Shopping Centres" },
  "major_shopping_centres": { title: "Major Shopping Centres" },
  "airports": { title: "Airports" },
  "pt": { title: "Rail (Outside CBD)" },
};

function dataset(config: CityConfig, data: any) {
  return {
    label: config.title,
    fill: false,
    lineTension: 0.1,
    borderColor: config.colour,
    borderCapStyle: 'butt',
    borderDash: [],
    borderDashOffset: 0.0,
    borderJoinStyle: 'miter',
    pointHoverRadius: 4,
    pointHoverBorderWidth: 2,
    pointHoverBackgroundColor: "#fafafa",
    pointRadius: 0,
    pointHitRadius: 5,
    data: data
  }
}

function average(array: number[]): number {
  return array.reduce((a: number, b: number) => a + b, 0) / array.length;
}

function sevenDayTrailingAverage(data: any[]): any[] {
  return data.map((e, index: number) => {
    return {
      t: e.t,
      // slice extracts up to but not including end
      y: average(data.slice(index - 6, index + 1).map(e => e.y))
    }
  });
}

function chartData(type: string) {
  return {
    datasets: Object.keys(cities).map((city) => dataset(cities[city], sevenDayTrailingAverage(data[`${city}_${type}`]))),
  }
};

defaults.global.defaultFontFamily = `
  -apple-system,
  BlinkMacSystemFont,
  'Segoe UI',
  'Roboto',
  'Helvetica Neue', Arial, sans-serif,
  'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'
`;
defaults.global.title.fontSize = 14;
defaults.global.tooltips.backgroundColor = "#fcfcfc";
defaults.global.tooltips.titleFontColor = "#000";
defaults.global.tooltips.bodyFontColor = "#000";
defaults.global.tooltips.bodySpacing = 4;
defaults.global.tooltips.borderColor = "#999";
defaults.global.tooltips.borderWidth = 0.5;

function formatXTick(_: any, index: any, values: any) {
  const date = moment(values[index].value);
  const startOfMonth = date.clone().startOf('month'); // moments are mutable
  const startOfYear = date.clone().startOf('year');
  if (date.isSame(startOfYear) || date.isSame('2020-03-01')) {
    return date.format('MMM. YYYY')
  } else if (date.isSame(startOfMonth)) {
    return date.format('MMM.')
  } else {
    return null;
  }
}

function chartOptions(type: string, yMax: number) {
  const gridColor = "#DCDCDC";
  return {
    title: {
      display: true,
      text: charts[type].title
    },
    scales: {
      xAxes: [{
        type: 'time',
        time: { unit: 'month', parser: 'YYYY-MM-DD' },
        ticks: {
          callback: formatXTick,
          min: '2020-02-20',
          max: '2021-12-13',
        },
        gridLines: {
          color: gridColor,
          zeroLineColor: gridColor,
          drawOnChartArea: false
        },
      }],
      yAxes: [{
        ticks: {
          callback: (value: any, index: any, values: any) => (value * 100).toFixed(0) + '%',
          min: 0,
          max: yMax,
          stepSize: 0.25
        },
        gridLines: {
          color: gridColor,
          zeroLineColor: gridColor,
        },
      }]
    },
    legend: {
      display: true,
      position: 'top'
    },
    tooltips: {
      mode: 'index',
      callbacks: {
        title: (tooltipItem: any, data: any) => {
          let date = tooltipItem[0].xLabel;
          const options: Intl.DateTimeFormatOptions = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' };
          if (date) {
            return (new Date(date)).toLocaleDateString('en-AU', options);
          }
          return '';
        },
        label: (tooltipItem: any, data: any) => {
          let label = data.datasets[tooltipItem.datasetIndex].label;
          if (label) {
            return ` ${label}: ${(tooltipItem.yLabel * 100).toFixed(0)}%`;
          }
          return '';
        }
      }
    }
  }
};

function Chart(props: { type: string, yMax: number }) {
  return (
    <div className="py-3 pr-2">
      <Line height={225} data={chartData(props.type)} options={chartOptions(props.type, props.yMax)} />
    </div>
  );
}

function App() {
  return (
    <>
      <Navbar className='main-nav'>
        <Navbar.Brand href="https://veitchlister.com.au"><img
          alt=""
          src="https://veitchlister.com.au/wp-content/themes/vlc/img/logo-reverse.svg"
          width="130"
          height="70"
          className="d-inline-block align-top"
        /></Navbar.Brand>
      </Navbar>
      <Container className="my-4">
        <Row>
          <Col>
            <h1>COVID-19 Mobility Trends Dashboard</h1>
            <p>
              This dashboard reports trends in mobility from the beginning of January 2020 and is updated each week. The data contained here provides indexed numbers of mobile devices (after data cleaning) seen in Sydney, Melbourne, Brisbane, and Perth across the following categories:
            </p>
            <ol>
              <li>Within the CBD</li>
              <li>The domestic and international terminals of the city's major airport</li>
              <li>Major shopping centres (8–13 for each city)</li>
              <li>Suburban shopping centres (12–20 for each city)</li>
              <li>A sample of rail stations outside the CBD (14–17 for each city)</li>
            </ol>
            <p>
              Each data point is a seven day (trailing) average, and is indexed such that 100 = The daily average from the week of February 10th to 16th 2020.
            </p>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}><Chart type='cbd' yMax={1.5} /></Col>
          <Col xs={12} md={6}><Chart type='airports' yMax={1.5} /></Col>
          <Col xs={12} md={6}><Chart type='major_shopping_centres' yMax={2} /></Col>
          <Col xs={12} md={6}><Chart type='shopping_centres' yMax={2} /></Col>
          <Col xs={12} md={6}><Chart type='pt' yMax={1.5} /></Col>
        </Row>
        <Row className="pt-4">
          <Col>
            <p>
              If this data or any other analysis of it is of further interest to your organisation, please get in touch to find out how we can help.
            </p>
          </Col>
        </Row>
        <Row>
          <Col><Button className="my-2 text-white" variant="primary" href="https://veitchlister.com.au/contact-us/">Get in touch</Button></Col>
        </Row>
        <Row className="pt-4">
          <Col>
            <p><small>Last updated: 2021-12-20</small></p>
            <p>
              <small>
                This data is an anonymised summary of de-personalised location based services data which is licensed by VLC from a third-party. Location based services (LBS) data is gathered from smart phone applications (apps) that use sensors within mobile devices including Global Positioning System (GPS) sensors to estimate the location of the device. App publishers enter into commercial agreements with data aggregators who combine data from multiple publishers and license the combined dataset under strict conditions to organisations which analyse the data. VLC has processed the raw location trace level to create useful data summaries. Further, outputs produced for reporting are anonymised through aggregation in order to prevent identification of individuals. Data cleaning techniques applied filter the data for location events which appear erroneous and adjust for changes in the underlying supply base.
              </small>
            </p>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default App;

